import { useQuery, useMutation } from '@apollo/client';
import type { RenderParams } from '@seek/forms-ui';
import { ContractsApi } from '@seek/sbiz-shared-types';
import { required } from '@seek/validators-js';
import {
  Stack,
  Inline,
  Box,
  Button,
  Loader,
  Text,
  useToast,
  Divider,
  Page,
  Hidden,
  PageBlock,
} from 'braid-design-system';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Header from 'src/components/Header/Header';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import Routes from 'src/config/routes';
import { useCookiesStrict } from 'src/hooks/cookies';
import type { User } from 'src/types/user';
import { createDotNetLinkUrl } from 'src/utils';
import {
  AnalyticsEvent,
  fireOnboardingCompleteEvent,
  fireOnboardingCompleteSemEvent,
  fireRegistrationEvent,
} from 'src/utils/analytics';
import { clearSelectedProduct } from 'src/utils/cookies';
import { hasConsented } from 'src/utils/validators';

import BusinessDetails from './components/BusinessDetails/BusinessDetails';
import { EmailOptIn } from './components/EmailOptIn/EmailOptIn';
import PhoneNumberDetails from './components/PhoneNumber/PhoneNumber';
import UserName from './components/UserName/UserName';
import UserType from './components/UserType/UserType';
import ONBOARD_USER from './queries/onboardUser';
import USER_DETAILS from './queries/userDetails';
import {
  FormProvider,
  isEmailConsentRequired,
  mapImAToUserRegistrationType,
  type OnboardingFormData,
  useField,
} from './utils';

const OnboardingForm = ({ handleSubmit }: RenderParams<OnboardingFormData>) => {
  const urlLocation = useLocation();
  const { cookies, setCookie, removeCookie } = useCookiesStrict([
    'SelectedProduct',
    'OnboardPageSource',
    'HasVisitedOnboard',
    'HasOnboarded',
  ]);
  const { returnUrl } = queryString.parseUrl(urlLocation.search).query;
  const stringUrl = returnUrl as string;
  const [onboardUser] = useMutation(ONBOARD_USER);
  const [onboardingInProgress, setOnboardingInProgress] = useState(false);
  const showToast = useToast();
  const onboardInProgressRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (cookies.OnboardPageSource && !cookies.HasVisitedOnboard) {
      setCookie('HasVisitedOnboard', true);
      fireRegistrationEvent(AnalyticsEvent.RegistrationCompleteSem);
    }
  }, [cookies.OnboardPageSource, cookies.HasVisitedOnboard, setCookie]);

  const submit = async (formData: OnboardingFormData) => {
    setOnboardingInProgress(true);

    onboardInProgressRef.current?.scrollIntoView({ block: 'end' });

    const input = {
      ...formData,
      emailOptIn: formData.emailOptIn ?? false,
    };

    try {
      await onboardUser({ variables: { input } });
    } catch {
      showToast({
        tone: 'critical',
        message:
          'There was an unexpected issue. Please try again or contact our support.',
        action: {
          label: 'Contact us',
          onClick: () => window.location.assign('/contact-us'),
        },
        data: { testid: 'onboarding-error' },
      });
      setOnboardingInProgress(false);
      return {};
    }
    await fireOnboardingCompleteEvent(formData.userRegistrationType);

    if (cookies.OnboardPageSource && !cookies.HasOnboarded) {
      await fireOnboardingCompleteSemEvent(formData.userRegistrationType);
      setCookie('HasOnboarded', true);
      removeCookie('OnboardPageSource');
    }

    if (returnUrl) {
      window.location.assign(stringUrl);
    } else if (formData.userRegistrationType === 'PrivateSeller') {
      if (typeof cookies.SelectedProduct !== 'string') {
        window.location.assign(Routes.DASHBOARD);
        return {};
      }

      if (cookies.SelectedProduct === ContractsApi.ProductIds.Freemium) {
        clearSelectedProduct();
        window.location.assign(Routes.FREEMIUM_CONTRACT);
      } else {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('productToken', cookies.SelectedProduct);
        window.location.assign(
          `${Routes.PAYMENT}?${newSearchParams.toString()}`,
        );
      }
    } else if (formData.userRegistrationType === 'Buyer') {
      window.location.assign(createDotNetLinkUrl('/profile/saved-searches/'));
    } else {
      window.location.assign(createDotNetLinkUrl('/sign-up/advertiser/'));
    }

    return {};
  };

  const userRegistrationType = useField({
    id: 'userRegistrationType',
    validators: [required],
  });

  // WARNING: we influence the ".valid" property of this field by the below "isEmailOptInUseFieldValid"
  // We do this as the form's internal state doesn't reevaluate "validatorProps"
  // when dependent values change leading to stale validation messages
  const emailOptInUseField = useField({
    id: 'emailOptIn',
    validators: [hasConsented],
    validatorProps: {
      userRegistrationType: userRegistrationType.value,
    },
  });
  const isEmailOptInFieldValid = isEmailConsentRequired(
    userRegistrationType.value,
  )
    ? emailOptInUseField.valid
    : true;

  if (onboardingInProgress) {
    return (
      <Box
        ref={onboardInProgressRef}
        paddingY="large"
        paddingX="large"
        style={{ minHeight: 500 }}
        display="flex"
        justifyContent="center"
        alignItems="center"
        background="surface"
        marginY="small"
      >
        <Stack space="medium" align="center">
          <Loader />
          <Text>Creating your SEEK Business profile</Text>
        </Stack>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stack space="xlarge">
        <UserName />
        <UserType
          valid={userRegistrationType.valid}
          errorMessage={userRegistrationType.errorMessage}
          onChange={userRegistrationType.onChange}
          value={userRegistrationType.value}
        />
        {userRegistrationType.value === 'PrivateSeller' && <BusinessDetails />}
        {userRegistrationType.value !== 'PrivateSeller' &&
          userRegistrationType.value !== 'Buyer' && <PhoneNumberDetails />}

        <EmailOptIn
          userRegistrationType={userRegistrationType.value}
          valid={isEmailOptInFieldValid}
          errorMessage={emailOptInUseField.errorMessage}
          onChange={emailOptInUseField.onChange}
          value={emailOptInUseField.value ?? false}
        />

        <Stack space="medium">
          <Inline space="medium" collapseBelow="tablet">
            <Button
              type="submit"
              tone="formAccent"
              data={{
                htag: 'onboarding-continue-cta',
                analytics: 'onboarding-continue-cta',
              }}
            >
              Continue
            </Button>
          </Inline>
        </Stack>
      </Stack>
    </form>
  );
};

const Onboarding = () => {
  const urlLocation = useLocation();
  const { ima } = queryString.parseUrl(urlLocation.search).query;
  const { data, loading } = useQuery<User>(USER_DETAILS);
  const emailOptIn =
    data?.user?.subscription?.subscriptionStatus === 'Subscribed';
  const initialValues = {
    firstName: data?.user.firstName ?? '',
    lastName: data?.user.lastName ?? '',
    userRegistrationType: mapImAToUserRegistrationType(ima as string),
    businessName: undefined,
    abn: undefined,
    phoneNumber: undefined,
    phoneCountryCode: '61',
    emailOptIn,
  };
  return (
    <Page footer={<SiteWideFooter />} footerPosition="belowFold">
      <Header title="Onboarding" />

      <Hidden below="tablet">
        <Divider />
      </Hidden>

      <Box paddingTop="xlarge">
        <PageBlock width="small">
          {loading ? (
            <CustomLoader />
          ) : (
            <FormProvider initialValues={initialValues}>
              {(props) => <OnboardingForm {...props} />}
            </FormProvider>
          )}
        </PageBlock>
      </Box>
    </Page>
  );
};

export default Onboarding;
